import { useEffect, useMemo } from "react";
import { useKlausurEditing } from "@/features/klausur/context/klausurEditingProvider";
import type {
	AufgabenstellungDTO,
	MusterloesungDTO,
	SachverhaltDTO,
} from "@/features/korrektur/korrektur.types";
import KlausurErstellerKorrekturbogenEditor from "@/features/klausur/components/klausurErstellerKorrekturbogenEditor.component";
import { useKlausurEditorScroll } from "@/features/klausur/context/klausurEditorScrollProvider";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import EditableText from "@/components/EditableText";

/**
 * KlausurEditorContent
 *
 * This component is used to display the content of the klausur editor.
 * It contains the content of the klausur editor.
 */
export default function KlausurEditorContent() {
	const { data, isLoading, error } = useKlausurEditing();
	const { scrollToElement } = useKlausurEditorScroll();

	// Scroll to newly created aufgabenstellung/sachverhalt when selection event fires
	useEffect(() => {
		const handler = (e: Event) => {
			const detail = (e as CustomEvent).detail as
				| { aufgabenstellungId?: string; sachverhaltId?: string }
				| undefined;
			if (!detail) return;
			const id = detail.sachverhaltId || detail.aufgabenstellungId;
			if (id) scrollToElement(id, "instant");
		};
		document.addEventListener(
			"klausur-selection:set",
			handler as unknown as EventListener,
		);
		return () => {
			document.removeEventListener(
				"klausur-selection:set",
				handler as unknown as EventListener,
			);
		};
	}, [scrollToElement]);

	/**
	 * Sort the aufgabenstellungen by index
	 */
	const sortedAufgabenstellungen = useMemo(() => {
		return (data?.aufgabenstellungen ?? [])
			.slice()
			.sort((a, b) => a.index - b.index);
	}, [data]);

	if (isLoading)
		return <div className="text-sm text-muted-foreground">Lade Inhalte…</div>;
	if (error) return <div className="text-sm text-destructive">{error}</div>;
	if (!data)
		return (
			<div className="text-sm text-muted-foreground">
				Keine Inhalte vorhanden.
			</div>
		);

	// All create/update handlers and in-flight flags live in the provider now.

	return (
		<div className="h-full flex flex-col">
			<div className="flex-1 overflow-y-auto container-overflow-safe scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent flex flex-col gap-4 pr-1 rounded-lg">
				{sortedAufgabenstellungen.map((aufgabe, aIdx) => (
					<AufgabenstellungSection
						key={aufgabe.id}
						aufgabe={aufgabe}
						aufgabenIndex={aIdx}
					/>
				))}
			</div>
		</div>
	);
}

/**
 * AufgabenstellungSection
 *
 * This component is used to display the content of the aufgabenstellung section.
 * It contains the content of the aufgabenstellung section.
 */
function AufgabenstellungSection({
	aufgabe,
	aufgabenIndex,
}: {
	aufgabe: AufgabenstellungDTO;
	aufgabenIndex: number;
}) {
	const { updateAufgabenstellungTitle } = useKlausurEditing();
	const sachverhalteSorted = useMemo(() => {
		return aufgabe.sachverhalte.slice().sort((a, b) => a.index - b.index);
	}, [aufgabe.sachverhalte]);

	return (
		<div
			className="bg-background rounded-lg border border-border shadow-sm mt-2"
			data-scroll-id={aufgabe.id}
		>
			<div className="px-3 py-2 border-b bg-backgroundSecondary rounded-t-lg sticky top-0 z-20 bg-white text-black">
				<div className="flex items-center gap-2">
					<div className="font-semibold select-none">{aufgabenIndex + 1}.</div>
					<EditableText
						value={aufgabe.title || ""}
						placeholder="Aufgabenstellungstitel eingeben…"
						className="text-base font-semibold w-full"
						multiline={false}
						onCommit={(val) => updateAufgabenstellungTitle(aufgabe.id, val)}
					/>
				</div>
			</div>
			{sachverhalteSorted.map((sv, sIdx) => (
				<SachverhaltBlock
					key={sv.id}
					sachverhalt={sv}
					label={`${aufgabenIndex + 1}.${sIdx + 1}`}
				/>
			))}
		</div>
	);
}

/**
 * SachverhaltBlock
 *
 * This component is used to display the content of the sachverhalt block.
 * It contains the content of the sachverhalt block.
 */
function SachverhaltBlock({
	sachverhalt,
	label,
}: {
	sachverhalt: SachverhaltDTO;
	label: string;
}) {
	const {
		createMusterloesung,
		isCreatingMusterloesung,
		updateSachverhaltTitle,
	} = useKlausurEditing();
	const { scrollToElement } = useKlausurEditorScroll();
	const musterloesungenSorted = useMemo(() => {
		return (sachverhalt.musterloesungen ?? [])
			.slice()
			.sort((a, b) => a.index - b.index);
	}, [sachverhalt.musterloesungen]);

	return (
		<div className="bg-white rounded-md border" data-scroll-id={sachverhalt.id}>
			<div className="px-3 py-2 border-b bg-muted/30 sticky top-10 z-10 flex items-center justify-between gap-2 w-full bg-white">
				<div className="flex items-center gap-2 w-full">
					<div className="font-medium shrink-0 select-none">{label}</div>
					<EditableText
						value={sachverhalt.title || ""}
						placeholder="Sachverhaltstitel eingeben…"
						className="font-medium truncate w-full"
						multiline={false}
						onCommit={(val) => updateSachverhaltTitle(sachverhalt.id, val)}
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					onClick={async () => {
						const id = await createMusterloesung(sachverhalt.id);
						if (typeof id === "string") scrollToElement(id);
					}}
					disabled={isCreatingMusterloesung(sachverhalt.id)}
					className="shrink-0"
				>
					<Plus className="h-4 w-4 mr-1" /> Musterlösung hinzufügen
				</Button>
			</div>
			<div className="p-3 flex flex-col gap-3">
				{musterloesungenSorted.length > 0 ? (
					musterloesungenSorted.map((m) => (
						<MusterloesungBlock key={m.id} musterloesung={m} />
					))
				) : (
					<div className="text-xs text-muted-foreground px-1 py-2">
						Keine Musterlösungen vorhanden
					</div>
				)}
			</div>
		</div>
	);
}

/**
 * MusterloesungBlock
 *
 * This component is used to display the content of the musterloesung block.
 * It contains the content of the musterloesung block.
 */
function MusterloesungBlock({
	musterloesung,
}: {
	musterloesung: MusterloesungDTO;
}) {
	const {
		createKorrekturbogenItem,
		isCreatingItem,
		updateMusterloesungTitle,
		updateKorrekturbogenItemMaxPoints,
		updateKorrekturbogenItemText,
	} = useKlausurEditing();
	const { scrollToElement } = useKlausurEditorScroll();
	const itemsSorted = useMemo(() => {
		return (musterloesung.korrekturbogenItems ?? [])
			.slice()
			.sort((a, b) => a.index - b.index);
	}, [musterloesung.korrekturbogenItems]);

	return (
		<div className="flex flex-col gap-2">
			<KlausurErstellerKorrekturbogenEditor
				title={musterloesung.title}
				musterloesungId={
					musterloesung.korrekturMusterloesungId || musterloesung.id
				}
				korrekturbogenItems={itemsSorted}
				canEditStructure={true}
				onMusterloesungTitleUpdate={updateMusterloesungTitle}
				onItemMaxPointsUpdate={updateKorrekturbogenItemMaxPoints}
				onItemTextUpdate={updateKorrekturbogenItemText}
				onRegisterMusterloesungRef={(el) =>
					el?.setAttribute(
						"data-scroll-id",
						musterloesung.korrekturMusterloesungId || musterloesung.id,
					)
				}
				onRegisterItemRef={(id, el) => {
					if (!el) return;
					el.setAttribute("data-scroll-id", id);
				}}
			/>
			{itemsSorted.length === 0 && (
				<div className="text-xs text-muted-foreground px-1 py-2">
					Keine Korrekturbogen-Elemente vorhanden
				</div>
			)}
			<div className="flex justify-end">
				<Button
					variant="secondary"
					size="sm"
					onClick={async () => {
						const id = await createKorrekturbogenItem(musterloesung);
						typeof id === "string" && scrollToElement(id);
					}}
					disabled={isCreatingItem(
						musterloesung.korrekturMusterloesungId || musterloesung.id,
					)}
					className="shrink-0"
				>
					<Plus className="h-4 w-4 mr-1" /> Korrekturbogen-Element hinzufügen
				</Button>
			</div>
		</div>
	);
}
